import { useState } from 'react';
import { VBX, VBY, ChartAxes, ChartTooltip, hoverIndexFromEvent, linePath,
  ChartFrame,  spacedTicks,

} from '../bookings/chartParts';
import { axisTicks, fmtEur, fmtEurShort, niceScale, type TrendPoint } from './financesData';

// Revenue vs staff-cost over time. Revenue line (brand) above the wages line
// (terracotta, dashed); the gap between them is shaded — that gap is money
// after wages. Same .chart-* chrome as BookingsLineChart (VBX/VBY=100 viewBox,
// HTML-overlaid axes), with euro formatting and a net row in the tooltip.
export function FinancesTrendChart({ points, wagesEstimated }: { points: TrendPoint[]; wagesEstimated: boolean }) {
  const [hi, setHi] = useState<number | null>(null);
  if (points.length === 0) return <div className="bookings-empty">No data in this range.</div>;

  // The axis may need to reach BELOW ZERO: on the Received basis a month can be
  // negative when refunds or adjustments outweigh receipts.
  const { yMin, yMax, step } = niceScale(
    Math.max(1, ...points.map((p) => Math.max(p.revenue, p.wages))),
    Math.min(0, ...points.map((p) => Math.min(p.revenue, p.wages))),
  );
  const n = points.length;
  const span = yMax - yMin;
  const xAt = (i: number) => (n === 1 ? VBX / 2 : (i / (n - 1)) * VBX);
  const yAt = (v: number) => VBY - ((v - yMin) / span) * VBY;
  const zeroY = yAt(0);
  const line = (key: 'revenue' | 'wages') => linePath(n, (i) => [xAt(i), yAt(points[i]![key])]);
  const back = points.map((p, i) => ({ p, i })).reverse().map(({ p, i }) => 'L' + xAt(i).toFixed(2) + ',' + yAt(p.wages).toFixed(2)).join(' ');
  const netArea = `${line('revenue')} ${back} Z`;
  // The forward book: where committed-but-not-earned months begin. Shade that
  // region and drop a "now" divider so the tail doesn't read as a slump.
  const firstFwd = points.findIndex((p) => p.forward);
  const boundaryX = firstFwd <= 0 ? -1 : (xAt(firstFwd - 1) + xAt(firstFwd)) / 2;
  const hasForward = firstFwd >= 0 && boundaryX >= 0;

  const yTicks = axisTicks(yMax, step, yMin);
  // The shared label rule. This carried its own copy — without the guard, so a
  // series whose last point sat one step from a regular tick printed the two
  // labels on top of each other.
  const xTicks = spacedTicks(n, Math.ceil(n / 12)).map(({ i }) => ({ i, label: points[i]!.label }));
  const hp = hi != null ? points[hi]! : null;
  const leftPct = hi != null ? (hi / Math.max(1, n - 1)) * 100 : 0;
  const anchorPct = hp ? Math.max(0, Math.min(100, (yAt(Math.max(hp.revenue, hp.wages)) / VBY) * 100)) : 0;

  return (
    <ChartFrame
      className="bookings-chart fin-chart"
      title="Revenue & staff cost"
      legend={
        <div className="chart-legend">
          <span className="chart-legend-item"><span className="sw" style={{ background: 'var(--brand)' }} /> Revenue</span>
          <span className="chart-legend-item"><span className="sw dashed" style={{ background: 'var(--alert)' }} /> Wages{wagesEstimated ? ' (est.)' : ''}</span>
          <span className="chart-legend-item"><span className="sw" style={{ background: 'var(--brand-soft)' }} /> After wages</span>
          {hasForward && <span className="chart-legend-item"><span className="sw" style={{ background: 'var(--ink-mute)', opacity: 0.25 }} /> Booked ahead</span>}
        </div>
      }
    >
        <div
          className="chart-svg-wrap"
          onMouseLeave={() => setHi(null)}
          onMouseMove={(e) => setHi(hoverIndexFromEvent(e, n))}
        >
          <svg className="chart-svg" width="100%" height="100%" viewBox={`0 0 ${VBX} ${VBY}`} preserveAspectRatio="none" role="img" aria-label="Revenue and staff cost over time">
            {yTicks.map((v, i) => (
              <line key={'g' + i} x1="0" y1={yAt(v)} x2={VBX} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            {/* A stronger zero line when the axis dips below it, so a month
                under the line reads as money out rather than as a small month. */}
            {yMin < 0 && (
              <line x1="0" y1={zeroY} x2={VBX} y2={zeroY} stroke="var(--border-strong)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            )}
            {hasForward && (
              <>
                <rect x={boundaryX} y="0" width={VBX - boundaryX} height={VBY} fill="var(--ink-mute)" opacity="0.07" />
                <line x1={boundaryX} y1="0" x2={boundaryX} y2={VBY} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              </>
            )}
            <path d={netArea} fill="var(--brand-soft)" opacity="0.6" stroke="none" />
            <path d={line('revenue')} fill="none" stroke="var(--brand)" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
            <path d={line('wages')} fill="none" stroke="var(--alert)" strokeWidth="2" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
            {hi != null && (
              <line x1={xAt(hi)} y1="0" x2={xAt(hi)} y2={VBY} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
            )}
          </svg>
          {hp && (
            <>
              <span className="chart-focus-dot" style={{ left: (xAt(hi!) / VBX) * 100 + '%', top: (yAt(hp.revenue) / VBY) * 100 + '%', background: 'var(--brand)' }} />
              <span className="chart-focus-dot" style={{ left: (xAt(hi!) / VBX) * 100 + '%', top: (yAt(hp.wages) / VBY) * 100 + '%', background: 'var(--alert)' }} />
              <ChartTooltip
                head={hp.label + (hp.partial ? ' · so far' : hp.forward ? ' · booked ahead' : '')}
                rows={[
                  { key: 'revenue', sw: 'var(--brand)', label: 'Revenue', value: fmtEur(hp.revenue) },
                  { key: 'wages', sw: 'var(--alert)', label: 'Wages', value: fmtEur(hp.wages) },
                ]}
                total={{ label: 'After wages', value: fmtEur(hp.revenue - hp.wages) }}
                leftPct={leftPct}
                anchorPct={anchorPct}
              />
            </>
          )}
        </div>
      <ChartAxes yTicks={yTicks} xTicks={xTicks} seriesLength={n} formatY={fmtEurShort} />
    </ChartFrame>
  );
}
